import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';
import {
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Box,
  Chip,
  Tooltip,
  ListItem,
  useTheme,
  PopoverOrigin,
  Popover,
  Stack,
  SxProps,
  Theme,
  TableCellProps,
} from '@mui/material';

import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded';
import UnfoldLessRoundedIcon from '@mui/icons-material/UnfoldLessRounded';
import {
  calculateAccountSowStatus,
  calculateAccountStartEndDate,
  getFlagsToolTipMessage,
  sumUpAccountMembers,
  sumUpExpiringAccountMembers,
} from '../../../../redux/accounts';

import { MarginHighlightCell } from '../../../teamMemberView/components/MarginHighlightCell';
import {
  ExpandableTableRowChildProps,
  ExpandableTableRowProps,
} from '../../types';
import {
  AccountStatus,
  AlertType,
  CsatValues,
  ProjectOverview,
  SowStatus,
} from '../../../../types';

import {
  NOT_DEFINED_SCORE_VALUE,
  appConfig,
  statusColors,
} from '../../../../constants';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../../constants/paths';
import {
  scopeTooltipText,
  timelineTooltipText,
} from '../../AccountsList.utils';
import { isDateInCurrentWeek } from '../../../globalTracker/utils';

import {
  formatStringDate,
  getDateDifference,
  getRevenueLastInvoiceDateFormated,
  is,
} from '../../../../utils';
import AddCommentIcon from '@mui/icons-material/AddComment';
import { blue } from '@mui/material/colors';
import { AccountMenuDropDown } from '../AccountMenuDropDown/AccountMenuDropDown';
import { ReactComponent as RedFlagIcon } from '../../../../assets/redFlagIcon.svg';
import { ReactComponent as YellowFlagIcon } from '../../../../assets/yellowFlagIcon.svg';
import { priceFormat } from '../../../../components/tableCellRenders';

import WorkOffOutlinedIcon from '@mui/icons-material/WorkOffOutlined';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import { COLORS } from '../../../../utils/colors';

const AccountRow = ({
  onClick,
  account,
  isCLevelUser,
}: ExpandableTableRowProps) => {
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const handleSetOpenCard = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      event?.stopPropagation();
      setOpen(!open);
    },
    [setOpen, open],
  );

  const navigateToFlagsUrl = useCallback(
    (
      event: React.MouseEvent<HTMLDivElement, MouseEvent>,
      departmenId: string | undefined,
    ) => {
      event?.stopPropagation();
      if (is.dev || is.localhost) {
        window.open(
          `${appConfig.rockyDevUrl}/all-flags?status=in_progress&projects=${departmenId}`,
          '_blank',
          'noopener,noreferrer',
        );
      } else if (is.stage || is.preprod) {
        window.open(
          `${appConfig.rockyStageUrl}/all-flags?status=in_progress&projects=${departmenId}`,
          '_blank',
          'noopener,noreferrer',
        );
      } else {
        window.open(
          `${appConfig.rockyProdUrl}/all-flags?status=in_progress&projects=${departmenId}`,
          '_blank',
          'noopener,noreferrer',
        );
      }
    },
    [],
  );

  const accountSows = useMemo(() => {
    return calculateAccountSowStatus(account);
  }, [account]);

  const { accountStartDate, accountEndDate, endingWithinDays } = useMemo(() => {
    return calculateAccountStartEndDate(account);
  }, [account]);

  const expiringMembersCount = useMemo(() => {
    return sumUpExpiringAccountMembers(account);
  }, [account]);

  const flagsLength = useMemo(() => {
    return (account?.flagsCount?.red ?? 0) + (account?.flagsCount?.yellow ?? 0);
  }, [account]);

  const onClickAssessment = useCallback(
    ({
      accountId,
      projectId,
      event,
    }: {
      accountId: string;
      projectId?: string;
      event: Partial<Event>;
    }) => {
      !!event.stopPropagation && event.stopPropagation();

      let assessmentUrl = `${accountId}/${paths.projectStatus}`;

      if (projectId) {
        assessmentUrl += `&project=${projectId}`;
      }

      navigate(assessmentUrl);
    },
    [navigate],
  );

  const onClickAlert = useCallback(
    ({ accountId, event }: { accountId: string; event: Partial<Event> }) => {
      !!event.stopPropagation && event.stopPropagation();
      navigate(`${accountId}/${paths.risks}`);
    },
    [navigate],
  );

  return (
    <>
      <TableRow
        sx={{
          cursor: 'pointer',
          background: (theme) =>
            open
              ? theme.palette.background.default
              : account.alert?.createdAt
              ? COLORS.HIGHLIGHTS.CRITICAL
              : '',
          transition: 'background 0.25s',
          '&:hover': {
            background: (theme) => theme.palette.action.hover,
          },
          '& .MuiTableCell-root': {
            padding: '8px 7px',
          },
        }}
        onClick={onClick}
      >
        {/* Account Name */}
        <CustomTableCell>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              pl: 1.25,
            }}
          >
            {account.status === AccountStatus.Closed ? (
              <Tooltip title="Closed" color="disabled" placement="top">
                <WorkOffOutlinedIcon />
              </Tooltip>
            ) : (
              <GeneralScore
                accountId={account.id}
                onClickAssessment={onClickAssessment}
                score={account.latestGeneralAssessmentScore}
                alert={account.alert}
                onClickAlert={onClickAlert}
              />
            )}

            <ListItem sx={{ paddingY: 0 }}>
              <Tooltip title={account.name} placement="top">
                <Typography
                  whiteSpace="nowrap"
                  variant={open ? 'h5' : 'h6'}
                  sx={{
                    overflow: 'hidden',
                    maxWidth: '80px',
                    textOverflow: 'ellipsis',
                  }}
                  color={
                    account.status === AccountStatus.Closed
                      ? '#00000099'
                      : undefined
                  }
                >
                  {account.name}
                </Typography>
              </Tooltip>

              <IconButton
                aria-label="Expand/collapse icon"
                size="small"
                onClick={handleSetOpenCard}
              >
                {open ? <UnfoldLessRoundedIcon /> : <UnfoldMoreRoundedIcon />}
              </IconButton>

              <Typography whiteSpace="nowrap" variant="body2">
                {account.projects.length}
              </Typography>
            </ListItem>

            <TimelineScore
              accountId={account.id}
              onClickAssessment={onClickAssessment}
              score={account.latestTimelineAssessmentScore}
            />

            <ScopeScore
              accountId={account.id}
              onClickAssessment={onClickAssessment}
              score={account.latestScopeAssessmentScore}
            />
          </Box>
        </CustomTableCell>

        {/* Account Margin */}
        <MarginHighlightCell
          align="right"
          marginHighlights={account.grossMarginHighlights || []}
          value={account.grossMargin}
          marginCategory="Gross Margin"
        >
          {account.grossMargin == null ? 'NA' : `${account.grossMargin}%`}
        </MarginHighlightCell>

        {/* Account Margin */}
        <MarginHighlightCell
          align="right"
          marginHighlights={account.netMarginHighlights || []}
          value={account.netMargin}
          marginCategory="Net Margin"
        >
          {account.netMargin == null ? 'NA' : `${account.netMargin}%`}
        </MarginHighlightCell>

        {/* Project Revenue */}
        <CustomTableCell>
          <Box
            display="flex"
            flexDirection="row"
            alignItems="end"
            justifyContent="end"
          >
            <Typography
              align={'right'}
              sx={{ fontWeight: '500', marginRight: '-20px' }}
            >
              {priceFormat({
                value: account.projectedRevenue
                  ? Math.round(account.projectedRevenue)
                  : account.projectedRevenue,
              })}
            </Typography>
          </Box>
        </CustomTableCell>

        {/* Account Revenue */}
        <ActualRevenue
          revenueLastInvoiceDate={account.revenueLastInvoiceDate}
          revenue={account.pastRevenue}
        />

        {/* Account Headcount */}
        <CustomTableCell>
          <Box display="flex" flexDirection="row" gap="10px" alignItems={'end'}>
            <Typography whiteSpace="nowrap" variant="body1">
              {sumUpAccountMembers(account)}
            </Typography>
            <Typography
              whiteSpace="nowrap"
              variant="body2"
              sx={{
                ...((account.openPositions ?? 0) !== 0 && {
                  textDecoration: 'underline',
                }),
              }}
            >
              {(account.openPositions ?? 0) !== 0
                ? `+${account.openPositions ?? 0}`
                : '—'}
            </Typography>
          </Box>
        </CustomTableCell>

        {/* Flags Count */}
        <CustomTableCell customProps={{ align: 'left' }}>
          {flagsLength > 0 ? (
            <Tooltip
              title={getFlagsToolTipMessage(account?.flagsCount) ?? ''}
              placement="top"
            >
              <Box
                textAlign="center"
                display="flex"
                flexDirection="row"
                alignItems="center"
                justifyContent="flex-start"
                onClick={(e) => navigateToFlagsUrl(e, account.zohoId)}
              >
                <Typography whiteSpace="nowrap" variant="body1">
                  {flagsLength}
                </Typography>
                <span>
                  {account?.flagsCount?.red === 0 ? (
                    <YellowFlagIcon />
                  ) : (
                    <RedFlagIcon />
                  )}
                </span>
              </Box>
            </Tooltip>
          ) : (
            <Box
              sx={{ color: 'text.disabled', mr: 0.5 }}
              onClick={(e) => navigateToFlagsUrl(e, account.zohoId)}
            >
              &mdash;
            </Box>
          )}
        </CustomTableCell>

        {/* Account SOW Status */}
        <CustomTableCell>
          <Box textAlign="left" paddingRight={'16px'}>
            <Typography whiteSpace="nowrap" variant="body1">
              {accountSows.potential === account.projects.length
                ? SowStatus.Potential
                : accountSows.expired
                ? SowStatus.Expired
                : SowStatus.Active}
            </Typography>
            <Typography variant="body2">
              {!!accountSows.potential &&
                `${accountSows.potential} ${SowStatus.Potential} projects`}
            </Typography>
            {!!expiringMembersCount && (
              <Typography variant="body1" color={'red'}>
                {`${expiringMembersCount} expiring in 30 days`}
              </Typography>
            )}
          </Box>
        </CustomTableCell>

        {/* Account Forcasted End date */}
        <ForecastedEndDate
          endedAt={accountEndDate}
          startedAt={accountStartDate}
          endingWithinDays={endingWithinDays}
        />

        {/* Account PDM */}
        {isCLevelUser && <AssignedUser fullName={account.pdmFullName} />}

        {/* Account DM */}
        <AssignedUser fullName={account.dmFullName} />

        {/* Account CSAT */}
        <CustomTableCell customProps={{ align: 'center' }}>
          <CsatChip csat={account.csat} />
        </CustomTableCell>

        {/* DropDown Menu */}
        <CustomTableCell customProps={{ align: 'center' }}>
          <AccountMenuDropDown
            accountName={account.name}
            sharedDriveLink={account.sharedDriveLink}
            sowLink={account.sowLink}
            customLinks={account.customLinks}
            compensationToolLink={account.compensationToolLink}
            accountId={account.id}
          />
        </CustomTableCell>
      </TableRow>

      <ProjectRows
        open={open}
        account={account}
        onClickAssessment={onClickAssessment}
      />
    </>
  );
};

interface CustomTableCellProps {
  sx?: SxProps<Theme> | undefined;
  customProps?: TableCellProps;
}

const CustomTableCell: React.FC<PropsWithChildren<CustomTableCellProps>> = ({
  children,
  sx,
  customProps,
}) => {
  return (
    <TableCell
      sx={{
        paddingX: 0,
        paddingY: 1,
        ...sx,
      }}
      {...customProps}
    >
      {children}
    </TableCell>
  );
};

const ActualRevenue = ({
  revenueLastInvoiceDate,
  revenue,
}: {
  revenueLastInvoiceDate?: string | null;
  revenue?: number | null;
}) => {
  return (
    <CustomTableCell>
      <Tooltip
        title={getRevenueLastInvoiceDateFormated(
          revenueLastInvoiceDate ?? null,
        )}
        placement="top"
        disableHoverListener={!revenueLastInvoiceDate}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignItems="end"
          justifyContent="end"
        >
          <Typography
            align={'right'}
            sx={{ fontWeight: '500', marginRight: '-20px' }}
          >
            {priceFormat({
              value: revenue ? Math.round(revenue) : revenue,
            })}
          </Typography>
        </Box>
      </Tooltip>
    </CustomTableCell>
  );
};

const ProjectRows = ({
  account,
  open,
  onClickAssessment,
}: ExpandableTableRowChildProps) => {
  const navigate = useNavigate();

  const onSelectProject = useCallback(
    (project: ProjectOverview) => {
      navigate(`${paths.portfolio}/${account.id}/${project.id}`);
    },
    [navigate, account.id],
  );

  const onAddAssessment = useCallback(
    (accountId: string, projectId: string) => {
      navigate(`${accountId}/${projectId}/${paths.projectStatus}`);
    },
    [navigate],
  );

  if (!open) return <></>;

  return (
    <>
      {account.projects.map((project) => {
        const showAddCommentIcon =
          !project.latestGeneralAssessment ||
          !isDateInCurrentWeek(
            new Date(project.latestGeneralAssessment.forDate),
          );

        const endingWithinDays =
          project.endedAt === null
            ? undefined
            : getDateDifference(new Date(), new Date(project.endedAt));

        return (
          <TableRow
            sx={{
              cursor: 'pointer',
              transition: 'background 0.25s',
              '&:hover': {
                background: (theme: Theme) => theme.palette.action.hover,
              },
              '& .MuiTableCell-root': {
                padding: '8px 7px',
              },
            }}
            key={project.id}
            onClick={() => onSelectProject(project)}
          >
            {/* Project Name */}
            <TableCell>
              <Box display="flex" alignItems="center" ml={3.5}>
                <GeneralScore
                  projectId={project.id}
                  accountId={account.id}
                  onClickAssessment={onClickAssessment}
                  score={project.latestGeneralAssessment?.score}
                />

                <ListItem>
                  <Tooltip title={project.name} placement="top">
                    <Typography
                      whiteSpace="nowrap"
                      variant={open ? 'h5' : 'h6'}
                      sx={{
                        overflow: 'hidden',
                        maxWidth: '80px',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {project.name}
                    </Typography>
                  </Tooltip>
                </ListItem>

                {showAddCommentIcon && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    sx={{ minWidth: '20px' }}
                    justifyContent="flex-start"
                  >
                    <Tooltip title="Add assessment" placement="top">
                      <AddCommentIcon
                        sx={{
                          fontSize: '1rem',
                          color: blue[500],
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddAssessment(account.id, project.id);
                        }}
                      />
                    </Tooltip>
                  </Stack>
                )}

                <TimelineScore
                  accountId={account.id}
                  onClickAssessment={onClickAssessment}
                  score={project.latestTimelineAssessment?.score}
                />

                <ScopeScore
                  accountId={account.id}
                  onClickAssessment={onClickAssessment}
                  score={project.latestScopeAssessment?.score}
                />
              </Box>
            </TableCell>

            {/* Gross Margin */}
            <MarginHighlightCell
              align="right"
              marginHighlights={account.grossMarginHighlights || []}
              value={project.grossMargin}
              marginCategory="Gross Margin"
            >
              {project.grossMargin == null ? 'NA' : `${project.grossMargin}%`}
            </MarginHighlightCell>

            {/* Net Margin */}
            <MarginHighlightCell
              align="right"
              marginHighlights={account.netMarginHighlights || []}
              value={project.netMargin}
              marginCategory="Net Margin"
            >
              {project.netMargin == null ? 'NA' : `${project.netMargin}%`}
            </MarginHighlightCell>

            {/* Project Revenue */}
            <CustomTableCell>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="end"
                justifyContent="end"
              >
                <Typography
                  align={'right'}
                  sx={{ fontWeight: '500', marginRight: '-20px' }}
                >
                  {priceFormat({
                    value: project.projectedRevenue
                      ? Math.round(project.projectedRevenue)
                      : project.projectedRevenue,
                  })}
                </Typography>
              </Box>
            </CustomTableCell>

            {/* Project Revenue */}
            <ActualRevenue
              revenueLastInvoiceDate={project.revenueLastInvoiceDate}
              revenue={project.pastRevenue}
            />

            {/* Project Size */}
            <TableCell align="right">
              <Box display="flex" flexDirection="row" alignItems={'end'}>
                <Typography whiteSpace="nowrap" variant="body1">
                  {sumUpAccountMembers(account)}
                </Typography>
              </Box>
            </TableCell>

            <TableCell></TableCell>

            {/* Project SOW Status */}
            <TableCell>
              <Typography whiteSpace="nowrap" variant="body1" pl={0.5}>
                {project.sowStatus}
              </Typography>
              {!!project.membersWithSowToExpireWithinMonth && (
                <Typography variant="body1" pl={0.5} color={'red'}>
                  {`${project.membersWithSowToExpireWithinMonth} expiring within 30 days`}
                </Typography>
              )}
            </TableCell>

            {/* Project Forcasted End date */}
            <ForecastedEndDate
              endedAt={
                project.endedAt &&
                new Date(project.endedAt)?.toLocaleDateString('en-US')
              }
              startedAt={
                project.startedAt &&
                new Date(project.startedAt)?.toLocaleDateString('en-US')
              }
              endingWithinDays={endingWithinDays}
            />
            <TableCell colSpan={5}></TableCell>
          </TableRow>
        );
      })}
    </>
  );
};

interface ForecastedEndDateProps {
  startedAt: string | null;
  endedAt: string | null;
  endingWithinDays: number | undefined;
}

const ForecastedEndDate: React.FC<ForecastedEndDateProps> = ({
  startedAt,
  endedAt,
  endingWithinDays,
}) => {
  return (
    <CustomTableCell>
      <Box component="span" sx={{ color: 'text.disabled' }}>
        {!!!endedAt && !!!startedAt ? (
          <Typography whiteSpace="nowrap" variant="button">
            &mdash;
          </Typography>
        ) : (
          <>
            {!!startedAt && !!!endedAt ? (
              <Typography whiteSpace="nowrap" variant="button">
                &infin;
              </Typography>
            ) : (
              <>
                {!!endedAt && (
                  <Typography
                    whiteSpace="nowrap"
                    variant="body1"
                    color={
                      endingWithinDays
                        ? endingWithinDays > 30
                          ? 'black'
                          : 'red'
                        : 'black'
                    }
                  >
                    {endedAt}
                  </Typography>
                )}
                {!!startedAt && (
                  <Typography whiteSpace="nowrap" variant="body2">
                    {`Started ${startedAt}`}
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </CustomTableCell>
  );
};

interface ScoreProps {
  score?: number;
  onClickAssessment: (params: {
    accountId: string;
    projectId?: string | undefined;
    event: Partial<Event>;
  }) => void;
  accountId: string;
  projectId?: string;
  alert?: AlertType;
  onClickAlert?: ({
    accountId,
    event,
  }: {
    accountId: string;
    event: Partial<Event>;
  }) => void;
}

const GeneralScore: React.FC<ScoreProps> = ({
  score,
  onClickAssessment,
  accountId,
  projectId,
  alert,
  onClickAlert,
}) => {
  if (alert && onClickAlert) {
    return (
      <Tooltip
        title={`Alert since ${formatStringDate(alert.createdAt, 'MMM d')}`}
        placement="top"
      >
        <NotificationsActiveIcon
          sx={{ fontSize: '16px', color: statusColors[0] }}
          onClick={(e) => onClickAlert({ event: e, accountId })}
        />
      </Tooltip>
    );
  }

  switch (score) {
    case 0:
      return (
        <Tooltip title="General: red" placement="top">
          <ErrorRoundedIcon
            sx={{ fontSize: '16px', color: statusColors[0] }}
            onClick={(e) =>
              onClickAssessment({ event: e, accountId, projectId })
            }
          />
        </Tooltip>
      );
    case 1:
      return (
        <Tooltip title="General: yellow" placement="top">
          <WarningRoundedIcon
            sx={{
              fontSize: '16px',
              color: statusColors[score],
            }}
            onClick={(e) =>
              onClickAssessment({ event: e, accountId, projectId })
            }
          />
        </Tooltip>
      );
    case 2:
      return (
        <Tooltip title="General: green" placement="top">
          <CheckCircleRoundedIcon
            sx={{
              fontSize: '16px',
              color: statusColors[score],
            }}
            onClick={(e) =>
              onClickAssessment({ event: e, accountId, projectId })
            }
          />
        </Tooltip>
      );
    default:
      return (
        <Tooltip title="No status provided" placement="top">
          <FiberManualRecordIcon
            sx={{
              fontSize: '16px',
              color: statusColors[NOT_DEFINED_SCORE_VALUE],
            }}
            onClick={(e) =>
              onClickAssessment({ event: e, accountId, projectId })
            }
          />
        </Tooltip>
      );
  }
};

const TimelineScore: React.FC<ScoreProps> = ({
  onClickAssessment,
  score,
  accountId,
  projectId,
}: ScoreProps) => {
  return (
    <Tooltip title={timelineTooltipText(score)} placement="top">
      <FiberManualRecordIcon
        sx={{
          fontSize: '0.7rem',
          color: statusColors[score ?? NOT_DEFINED_SCORE_VALUE],
        }}
        onClick={(e) =>
          onClickAssessment({
            event: e,
            accountId,
            projectId,
          })
        }
      />
    </Tooltip>
  );
};

const ScopeScore: React.FC<ScoreProps> = ({
  onClickAssessment,
  score,
  accountId,
  projectId,
}: ScoreProps) => {
  return (
    <Tooltip title={scopeTooltipText(score)} placement="top">
      <FiberManualRecordIcon
        sx={{
          fontSize: '0.7rem',
          color: statusColors[score ?? NOT_DEFINED_SCORE_VALUE],
        }}
        onClick={(e) =>
          onClickAssessment({
            event: e,
            accountId,
            projectId,
          })
        }
      />
    </Tooltip>
  );
};

interface AssignedUserProps {
  fullName?: string;
}

const AssignedUser: React.FC<AssignedUserProps> = ({
  fullName,
}: AssignedUserProps) => {
  const [first, second] = useMemo(() => {
    if (!fullName) return [undefined, undefined];

    const arr = fullName.split(' ');

    const first = arr?.[0];
    const second = arr?.[1]
      ? `${arr[1]}${arr?.length > 2 ? '...' : ''}`
      : undefined;

    return [first, second];
  }, [fullName]);

  return (
    <CustomTableCell customProps={{ align: 'left' }}>
      {fullName ? (
        <Tooltip
          title={fullName.split(' ').length > 2 ? fullName : ''}
          placement="top"
        >
          <Typography
            whiteSpace="nowrap"
            variant="body1"
            textOverflow={'ellipsis'}
          >
            {first && (
              <>
                <span>{first}</span>
                <br />
              </>
            )}
            {second && (
              <>
                <span>{second}</span>
              </>
            )}
            {!first && !second ? 'Name not found' : ''}
          </Typography>
        </Tooltip>
      ) : (
        <Typography whiteSpace="nowrap" variant="body2">
          Not <br />
          Assigned
        </Typography>
      )}
    </CustomTableCell>
  );
};

interface CsatChipProps {
  csat?: CsatValues;
}

const CsatChip: React.FC<CsatChipProps> = ({ csat }) => {
  const csatAnchor = React.useRef<HTMLElement | null>(null);
  const { palette } = useTheme();
  const [openCsatPopover, setOpenCsatPopover] = useState(false);

  const handleCsatPopoverOpen = useCallback(() => {
    setOpenCsatPopover(true);
  }, []);

  const handleCsatPopoverClose = useCallback(() => {
    setOpenCsatPopover(false);
  }, []);

  const getBackgroundColorByScore = useCallback(
    (score: number): string => {
      score = +score.toFixed(0);
      return palette.scale10(score);
    },
    [palette],
  );

  return (
    <>
      {!!(csat && Object.values(csat).some((c) => !!c)) ? (
        <Box ref={csatAnchor}>
          <Chip
            label={
              <Typography
                variant="body1"
                sx={{ fontSize: '13px', textAlign: 'center' }}
              >
                {csat.latest}
              </Typography>
            }
            sx={{
              backgroundColor: getBackgroundColorByScore(csat.latest ?? 0),
              width: '30px',
              height: '30px',
              borderRadius: '8px',
              color: csat.latest ? 'white' : 'text.disabled',
              '.MuiChip-label': {
                padding: 0,
              },
            }}
            onMouseEnter={handleCsatPopoverOpen}
            onMouseLeave={handleCsatPopoverClose}
          />
          <CsatPopover
            anchorEl={csatAnchor}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={openCsatPopover}
            transformOrigin={{
              vertical: 'bottom',
              horizontal: 5,
            }}
            handlePopoverOpen={handleCsatPopoverOpen}
            handlePopoverClose={handleCsatPopoverClose}
            csat={csat}
          />
        </Box>
      ) : (
        <Tooltip title="No reviews with score">
          <Typography
            whiteSpace="nowrap"
            variant="h3"
            pl={0.5}
            sx={{ color: 'text.disabled' }}
          >
            &mdash;
          </Typography>
        </Tooltip>
      )}
    </>
  );
};

interface CsatPopoverProps {
  anchorEl: React.MutableRefObject<HTMLElement | null>;
  anchorOrigin: PopoverOrigin;
  open: boolean;
  transformOrigin: PopoverOrigin;
  handlePopoverOpen: () => void;
  handlePopoverClose: () => void;
  csat: CsatValues;
}

const CsatPopover: React.FC<CsatPopoverProps> = ({
  anchorEl,
  anchorOrigin,
  open,
  transformOrigin,
  handlePopoverOpen,
  handlePopoverClose,
  csat,
}) => {
  const onMouseEnter = useCallback(() => {
    handlePopoverOpen();
  }, [handlePopoverOpen]);

  const onMouseLeave = useCallback(() => {
    handlePopoverClose();
  }, [handlePopoverClose]);

  return (
    <Popover
      style={{ pointerEvents: 'none' }}
      open={open}
      anchorEl={anchorEl.current}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      PaperProps={{
        onMouseEnter: onMouseEnter,
        onMouseLeave: onMouseLeave,
        sx: {
          pointerEvents: 'auto',
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          padding: '0.5rem',
          gap: '1.5rem',
        },
      }}
    >
      <Box
        display="flex"
        padding="0 0.5rem"
        flexDirection="column"
        alignItems="flex-start"
      >
        <Typography display="inline" variant="h4">
          Average CSATs
        </Typography>
        <Typography display="inline" variant="body1">
          All time: {csat.all ?? 'NA'}
        </Typography>
        <Typography display="inline" variant="body1">
          Last 6 months: {csat.last6Months ?? 'NA'}
        </Typography>
        <Typography display="inline" variant="body1">
          Last 3 months: {csat.last3Months ?? 'NA'}
        </Typography>
      </Box>
    </Popover>
  );
};

export default AccountRow;
